import axios from 'axios';

import { httpApi } from './http';
import type { Blog, BlogListResult, BlogUpsertPayload } from '../types/blog';

const api = httpApi;

const pickArray = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.results)) return payload.results;
  if (payload.data && Array.isArray(payload.data.data)) return payload.data.data;
  if (payload.data && Array.isArray(payload.data.items)) return payload.data.items;
  return [];
};

const pickNumber = (payload: any, keys: string[], fallback: number) => {
  for (const k of keys) {
    const v = payload?.[k] ?? payload?.data?.[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return fallback;
};

const normalizeError = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const apiError = (error.response?.data as any)?.error || (error.response?.data as any)?.message;
    if (apiError) return String(apiError);
    if (status) return `Server error: ${status}`;
    if (error.request) return 'Network error: Unable to connect to server';
  }
  return fallback;
};

export class BlogService {
  static async listAdminBlogs(params: { page?: number; limit?: number } = {}): Promise<BlogListResult> {
    try {
      const response = await api.get<any>('/blog/admin', {
        params: {
          limit: params.limit ?? 20,
          page: params.page ?? 1,
        },
      });

      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) throw new Error(payload.error || payload.message || 'API request failed');

      const items = pickArray(payload) as Blog[];
      const page = pickNumber(payload, ['page'], params.page ?? 1);
      const pages = pickNumber(payload, ['pages', 'totalPages'], 1);
      const total = pickNumber(payload, ['total', 'count'], items.length);

      return { items, total, page, pages };
    } catch (e) {
      throw new Error(normalizeError(e, 'Failed to fetch blogs'));
    }
  }

  static async createBlog(payload: BlogUpsertPayload): Promise<Blog> {
    try {
      const response = await api.post<any>('/blog/admin', payload);
      const body = response.data;
      if (!body) throw new Error('No data received from server');
      if (body.success === false) throw new Error(body.error || body.message || 'API request failed');
      return (body.data || body.blog || body) as Blog;
    } catch (e) {
      throw new Error(normalizeError(e, 'Failed to create blog'));
    }
  }

  static async updateBlog(id: string, patch: Partial<BlogUpsertPayload>): Promise<Blog> {
    try {
      const response = await api.put<any>(`/blog/admin/${encodeURIComponent(id)}`, patch);
      const body = response.data;
      if (!body) throw new Error('No data received from server');
      if (body.success === false) throw new Error(body.error || body.message || 'API request failed');
      return (body.data || body.blog || body) as Blog;
    } catch (e) {
      throw new Error(normalizeError(e, 'Failed to update blog'));
    }
  }

  static async deleteBlog(id: string): Promise<void> {
    try {
      const response = await api.delete<any>(`/blog/admin/${encodeURIComponent(id)}`);
      const body = response.data;
      if (body && body.success === false) throw new Error(body.error || body.message || 'API request failed');
    } catch (e) {
      throw new Error(normalizeError(e, 'Failed to delete blog'));
    }
  }

  static async listPublishedBlogs(params: { page?: number; limit?: number } = {}): Promise<BlogListResult> {
    try {
      const response = await api.get<any>('/blog', {
        params: {
          limit: params.limit ?? 20,
          page: params.page ?? 1,
        },
      });

      const payload = response.data;
      if (!payload) throw new Error('No data received from server');
      if (payload.success === false) throw new Error(payload.error || payload.message || 'API request failed');

      const items = pickArray(payload) as Blog[];
      const page = pickNumber(payload, ['page'], params.page ?? 1);
      const pages = pickNumber(payload, ['pages', 'totalPages'], 1);
      const total = pickNumber(payload, ['total', 'count'], items.length);

      return { items, total, page, pages };
    } catch (e) {
      throw new Error(normalizeError(e, 'Failed to fetch published blogs'));
    }
  }

  static async getPublishedBlogBySlug(slug: string): Promise<Blog> {
    try {
      const response = await api.get<any>(`/blog/${encodeURIComponent(slug)}`);
      const body = response.data;
      if (!body) throw new Error('No data received from server');
      if (body.success === false) throw new Error(body.error || body.message || 'API request failed');
      return (body.data || body.blog || body) as Blog;
    } catch (e) {
      throw new Error(normalizeError(e, 'Failed to fetch blog'));
    }
  }
}
